import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { HORECA } from '@/constants/testIds';
import LutfiyeLogo from './LutfiyeSignature';
import CategoryModal from './CategoryModal';
import { API, resolveImg } from './adminConfig';
import { ArrowUpRight } from 'lucide-react';

export default function Categories() {
  const [cats, setCats] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/horeca/content`, { params: { section: 'urunler' } })
      .then((res) => setCats(res.data?.urunler || []))
      .catch(() => setCats([]));
  }, []);

  return (
    <section
      id="urunler"
      data-testid={HORECA.categoriesSection}
      className="relative paper-cream text-[#1F1F1F] py-24 md:py-32 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 mb-14">
          <div className="lg:col-span-7">
            <span className="eyebrow text-[#B08834] block mb-4">Ürün Grupları</span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-[64px] leading-[1.02] tracking-[-0.01em] font-light">
              Katkısız gruplar,
              <span className="italic text-[#B03A2E]"> tek imza</span>.
            </h2>
            <div className="mt-6 flex items-center gap-4">
              <LutfiyeLogo width="w-28 md:w-36" height="h-10 md:h-12" color="text-[#1F1F1F]" />
              <span className="w-8 h-px bg-[#B03A2E]" />
              <span className="eyebrow text-[#5A5145]">Karadeniz kökü</span>
            </div>
          </div>
          <p className="lg:col-span-5 lg:pt-4 text-[#5A5145] text-base md:text-lg leading-relaxed font-body font-light">
            Kahvaltıdan ikram menülerine, konsept sunumlardan private label
            koleksiyonlara. Her gruba tıklayın; alt ürünleri ve seçenekleri görün.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {cats.map((c, i) => (
            <motion.button
              key={c.id}
              type="button"
              data-testid={HORECA.categoryCard(c.id)}
              onClick={() => setSelected(c)}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.7, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
              className="group relative bg-[#FBF7EE] dark-frame block overflow-hidden flex flex-col text-left"
            >
              <div className="relative bg-[#FFFDF7] aspect-[4/5] overflow-hidden">
                {c.image_url && (
                  <img
                    src={resolveImg(c.image_url)}
                    alt={c.name}
                    className="absolute inset-0 w-full h-full object-contain object-center p-4 md:p-6 transition-transform duration-[1200ms] group-hover:scale-105"
                    loading="lazy"
                  />
                )}
                {c.tag && (
                  <div
                    className="absolute top-4 left-4 label-pill text-[#FBF7EE]"
                    style={{ background: c.accent || '#B08834' }}
                  >
                    {c.tag}
                  </div>
                )}
              </div>

              <div
                className="px-6 py-5 border-t-2 flex items-start justify-between gap-3 flex-1"
                style={{ borderColor: c.accent || '#B08834' }}
              >
                <div className="flex-1">
                  <h3 className="font-display text-2xl md:text-[26px] leading-tight text-[#1F1F1F]">
                    {c.name}
                  </h3>
                  {c.subtitle && (
                    <p className="text-xs md:text-sm font-body font-light mt-1.5 text-[#5A5145] leading-relaxed">
                      {c.subtitle}
                    </p>
                  )}
                  <div className="mt-3 flex items-center gap-3">
                    <span className="eyebrow text-[#5A5145]">
                      {(c.subcategories || []).length} alt ürün
                    </span>
                    <span
                      className="h-px w-6 group-hover:w-14 transition-all duration-500"
                      style={{ background: c.accent || '#B08834' }}
                    />
                  </div>
                </div>
                <ArrowUpRight className="w-5 h-5 text-[#1F1F1F]/60 group-hover:text-[#B03A2E] transition-all duration-500 group-hover:translate-x-1 group-hover:-translate-y-1 flex-shrink-0 mt-1" />
              </div>
            </motion.button>
          ))}
        </div>

        <div className="mt-16 flex flex-col md:flex-row items-center justify-between gap-6 pt-10 border-t border-[#1F1F1F]/15">
          <p className="text-[#5A5145] text-base md:text-lg font-body max-w-2xl">
            Aradığınız özel bir ürün mü var? Private label ile
            <span className="text-[#B03A2E]"> markanıza özel</span> üretim yapıyoruz.
          </p>
          <a
            href="#iletisim"
            className="inline-flex items-center gap-3 border border-[#1F1F1F] text-[#1F1F1F] hover:bg-[#1F1F1F] hover:text-[#FBF7EE] px-8 py-4 text-xs tracking-[0.2em] transition-colors font-body whitespace-nowrap"
          >
            ÖZEL ÜRETİM KONUŞ
          </a>
        </div>
      </div>

      <CategoryModal category={selected} onClose={() => setSelected(null)} />
    </section>
  );
}
